/**
 * The ámbito efectivo of a person — DERIVED, and unmistakably read-only.
 *
 * IT RENDERS NO FORM CONTROL OF ANY KIND
 * --------------------------------------
 * Not an `<input>`, not a `<select>`, not a `<textarea>` — and not a
 * DISABLED one either. `__tests__/effective-scope.test.tsx` renders this
 * component with `react-dom/server` and asserts the markup contains none
 * of those tags at all. Three structural reasons, none of them a matter
 * of taste:
 *
 * 1. `users.effective_scope_id` is DENORMALISED and recomputed on every
 *    membership or rules change. An id typed here would be reverted by
 *    the next recompute with nothing on screen saying so.
 * 2. A derived id is `eff_{sha256[:12]}` — computed FROM the normalised
 *    rules — so editing it cannot mean anything. The rules are the
 *    source; the id is their fingerprint.
 * 3. A disabled control still asserts "this is a field somebody could
 *    fill". The codebase already makes this distinction on purpose: the
 *    role-defaults admin card renders `<span>` while the user card
 *    renders `<button>`.
 *
 * The only affordance is a LINK, which navigates to the groups. Changing
 * a person's scope means changing their groups; there is no other lever
 * and the card must not imply one.
 *
 * A PERSON IN NO GROUP
 * --------------------
 * `effectiveScopeId === null` means NO GROUP REPORTS ONE. It is not
 * "Todo" and it is not "none": the builder exposes no endpoint carrying
 * `users.effective_scope_id` for a person outside every group, so the
 * only honest rendering is a warning that says the scope is unknown here
 * AND that a person in no group is granted no report by any group. The
 * decisions log records that the column ships `NOT NULL DEFAULT 'all'`
 * and that the default is fail-open once its writer is wired — which is
 * exactly why this card must not paint a confident, neutral "Todo".
 */

import { StatusChip } from "@pervasivemind/admin-ui";
import { Link } from "react-router-dom";

import { EFFECTIVE_SCOPE_ID_PREFIX, SCOPE_ALL } from "@/lib/access-api";
import type { TFn } from "@/pages/admin-access/access-status";

export interface EffectiveScopeGroup {
  id: string;
  name: string;
  scope_id: string;
  scope_name: string;
}

/** What the card is showing, as one closed word. Exported so the test
 *  can enumerate the three renderings without guessing at the markup. */
export type EffectiveScopeKind = "unknown" | "all" | "derived" | "explicit";

export function effectiveScopeKind(
  effectiveScopeId: string | null,
  groups: readonly EffectiveScopeGroup[],
): EffectiveScopeKind {
  if (effectiveScopeId === null || groups.length === 0) return "unknown";
  if (effectiveScopeId === SCOPE_ALL) return "all";
  return effectiveScopeId.startsWith(EFFECTIVE_SCOPE_ID_PREFIX)
    ? "derived"
    : "explicit";
}

export function EffectiveScopeCard({
  effectiveScopeId,
  groups,
  editGroupsHref,
  t,
}: {
  effectiveScopeId: string | null;
  groups: readonly EffectiveScopeGroup[];
  /** Where "Editar grupos" goes. A link, never a form. */
  editGroupsHref: string;
  /** Injected rather than taken from `useT()` so the read-only
   *  assertion can render this component with `react-dom/server` and
   *  read the MARKUP, which is the only place a disabled control would
   *  show up. Same reason the Dimensiones cells take it. */
  t: TFn;
}): JSX.Element {
  const kind = effectiveScopeKind(effectiveScopeId, groups);

  return (
    <section
      aria-label={t("access.people.effective.title")}
      className="rounded-lg border border-neutral-200 bg-white p-4"
    >
      <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
        {t("access.people.effective.eyebrow")}
      </p>

      {kind === "unknown" ? (
        <div className="mt-1 space-y-2">
          <StatusChip
            label={t("access.people.effective.unknown")}
            tone="warning"
            title={t("access.people.effective.unknownHint")}
          />
          <p className="text-[13px] text-neutral-600">
            {t("access.people.effective.noGroups")}
          </p>
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <StatusChip
              label={
                kind === "all"
                  ? t("access.scopes.all")
                  : t("access.people.effective.name", { n: groups.length })
              }
              tone={kind === "all" ? "warning" : "info"}
              title={
                kind === "all"
                  ? t("access.people.effective.allHint")
                  : undefined
              }
            />
            <span className="font-mono text-[13px] text-neutral-500">
              {effectiveScopeId}
            </span>
          </div>

          {kind === "derived" && (
            <p className="text-[13px] text-neutral-600">
              {t("access.people.effective.derived")}
            </p>
          )}

          <dl className="space-y-1">
            {groups.map((g) => (
              <div key={g.id} className="flex flex-wrap items-baseline gap-2">
                <dt className="text-[13px] font-semibold text-neutral-700">
                  {g.name}
                </dt>
                <dd className="text-[13px] text-neutral-600">
                  {g.scope_id === SCOPE_ALL ? t("access.scopes.all") : g.scope_name}{" "}
                  <span className="font-mono text-neutral-500">{g.scope_id}</span>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      <p className="mt-3 text-[13px] text-neutral-600">
        {t("access.people.effective.computed")}
      </p>

      <p className="mt-2">
        <Link
          to={editGroupsHref}
          className="text-sm font-medium text-primary-ink underline underline-offset-2"
        >
          {t("access.people.effective.editGroups")}
        </Link>
      </p>
    </section>
  );
}
